import { Component, Inject, OnInit, AfterViewChecked, ChangeDetectorRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs';
import { first } from 'rxjs/operators';
import { indicate } from 'src/app/operators';
import { EsraPipingAPIClient, CommonNoteViewModel } from 'src/app/shared/models/autogenerated-piping';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

@Component({
    selector: 'app-common-notes-add-edit',
    templateUrl: './common-notes-add-edit.component.html',
    styleUrls: ['./common-notes-add-edit.component.scss'],
    standalone: false
})
export class CommonNotesAddEditComponent implements OnInit, AfterViewChecked {
  codeInput: string;
  noteInput: string;
  inputFormGroup: UntypedFormGroup;
  commonNoteViewModelToUpdate: CommonNoteViewModel | null = null;
  updateNote: CommonNoteViewModel;
  title: string;
  modalStringReply: string;
  genericMissingValueMessage: string = 'This is a required field';

  submitClicked: boolean = false;
  isCreateMode: boolean = false;
  isEditMode: boolean = false;
  saving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly dialogRef: MatDialogRef<CommonNotesAddEditComponent, boolean>,
    private readonly esraApiClient: EsraPipingAPIClient,
    private readonly snackBarService: SnackBarService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) private readonly commonNote: CommonNoteViewModel | null
  ) {}

  ngOnInit(): void {
    this.commonNoteViewModelToUpdate = this.commonNote ? new CommonNoteViewModel(this.commonNote) : null;
    this.isEditMode = this.commonNoteViewModelToUpdate !== null;
    this.isCreateMode = !this.isEditMode;

    if (this.isCreateMode) {
      this.title = 'Create a new common note';
    } else if (this.commonNoteViewModelToUpdate) {
      this.title = `Edit common note ${this.commonNoteViewModelToUpdate.codeId}`;
    }

    this.initializeFormControls();
    if (this.isEditMode) {
      this.setFormValues();
    }
  }

  ngAfterViewChecked(): void {
    this.changeDetectorRef.detectChanges();
  }

  initializeFormControls(): void {
    if (this.isEditMode) {
      this.inputFormGroup = new UntypedFormGroup({
        codeControl: new UntypedFormControl({value: this.codeInput, disabled: true}, [Validators.required]),
        noteControl: new UntypedFormControl(this.noteInput, [Validators.required])
      });
    } else {
      this.inputFormGroup = new UntypedFormGroup({
        codeControl: new UntypedFormControl(this.codeInput, [Validators.required]),
        noteControl: new UntypedFormControl(this.noteInput, [Validators.required])
      });
    }
  }

  setFormValues(): void {
    if (!this.commonNoteViewModelToUpdate) {
      return;
    }

    this.inputFormGroup.setValue({
      codeControl: this.commonNoteViewModelToUpdate.codeId,
      noteControl: this.commonNoteViewModelToUpdate.note
    });
  }

  submitNote(): void {
    this.submitClicked = true;

    if (this.inputFormGroup.invalid) {
      this.inputFormGroup.markAllAsTouched();
    }

    if (this.inputFormGroup.valid && this.isCreateMode) {
      const commonNoteVM: CommonNoteViewModel = new CommonNoteViewModel({
        codeId: this.inputFormGroup.controls['codeControl'].value,
        note: this.inputFormGroup.controls['noteControl'].value,
        isActive: true
      });

      this.esraApiClient.createCommonNote(commonNoteVM).pipe(
        indicate(this.saving$),
        first()
      ).subscribe({
          next: () => {
            this.snackBarService.showSnackBar(
              true,
              `Common note ${commonNoteVM.codeId} was created successfully`
            );

            this.dialogRef.close(true);
          },
          error: (err: any) => {
            const message = err.response || err.error || 'The application encountered an error and cannot complete your request. Please try again later.';
            this.snackBarService.showSnackBar(
              true,
              message,
              'mat-snack-bar-error'
            );
          }
        });
    }

    const commonNoteToUpdate: CommonNoteViewModel | null = this.commonNoteViewModelToUpdate;
    if (this.inputFormGroup.valid && this.isEditMode && commonNoteToUpdate) {
      commonNoteToUpdate.codeId = this.inputFormGroup.controls['codeControl'].value;

      commonNoteToUpdate.note = this.inputFormGroup.controls['noteControl'].value;

      this.esraApiClient.createNewCommonNoteVersion(commonNoteToUpdate).pipe(
        indicate(this.saving$),
        first()
      ).subscribe({
          next: () => {
            this.snackBarService
              .showSnackBar(
                true,
                `Common note ${commonNoteToUpdate.codeId} updated successfully`
              );
              this.dialogRef.close(true);
          },
          error: (err: any) => {
            const message = err.response || err.error || 'The application encountered an error and cannot complete your request. Please try again later.';
            this.snackBarService
              .showSnackBar(true, message, 'mat-snack-bar-error')
          }
        });
    }
  }

  closeDialog(): void {
    this.dialogRef.close(false);
  }
}
